import { component$, Slot } from "@builder.io/qwik";
import type {
  SidebarMovieItemProps,
  SidebarMoviesProps,
  SidebarPaginationButtonProps,
} from "./types";
import CoverPlaceholder from "~/assets/cover-placeholder.png?w=240&jsx";
import {
  ACTOR_DIRECTOR_COVER_HEIGHT,
  ACTOR_DIRECTOR_COVER_WIDTH,
  CHANNEL_LOGO_HEIGHT,
  CHANNEL_LOGO_WIDTH,
} from "~/constants/images";

const SidebarMovieItem = ({
  url,
  movieTitle,
  backdropUrl,
  channelLogoUrl,
  scheduledTime,
  channelName,
}: SidebarMovieItemProps) => {
  return (
    <a
      href={url}
      class="sgtv-group sgtv-relative sgtv-z-20 sgtv-block sgtv-overflow-hidden sgtv-pt-[140%] sgtv-text-white hover:sgtv-text-accent-500"
    >
      {backdropUrl ? (
        <img
          alt={movieTitle}
          src={backdropUrl}
          width={ACTOR_DIRECTOR_COVER_WIDTH}
          height={ACTOR_DIRECTOR_COVER_HEIGHT}
          class="sgtv-absolute sgtv-left-0 sgtv-top-0 sgtv-h-full sgtv-max-h-full sgtv-w-full sgtv-max-w-full sgtv-object-cover"
        />
      ) : (
        <CoverPlaceholder
          alt=""
          class="sgtv-absolute sgtv-left-0 sgtv-top-0 sgtv-h-full sgtv-max-h-full sgtv-w-full sgtv-max-w-full sgtv-object-cover"
        />
      )}
      <div class="sgtv-absolute sgtv-bottom-0 sgtv-flex sgtv-w-full sgtv-items-center sgtv-bg-black/80 sgtv-p-[3%]">
        <div class="sgtv-flex sgtv-h-full sgtv-w-1/2 sgtv-items-center">
          <img
            alt={channelName}
            src={channelLogoUrl}
            width={CHANNEL_LOGO_WIDTH}
            height={CHANNEL_LOGO_HEIGHT}
            class="sgtv-max-w-17/20 sgtv-inline-block sgtv-h-auto sgtv-w-auto sgtv-transition-all sgtv-duration-[600ms] sgtv-ease-ease xs:sgtv-max-w-full"
          />
        </div>
        <span class="sgtv-inline-block sgtv-w-1/2 sgtv-truncate sgtv-pr-1.5 sgtv-text-right sgtv-font-helvetica sgtv-text-base sgtv-leading-tight sgtv-transition-all sgtv-duration-[600ms] sgtv-ease-ease group-hover:sgtv-text-accent-500 xs:sgtv-text-3xl md:sgtv-text-base">
          {scheduledTime}
        </span>
      </div>
    </a>
  );
};

const SidebarPaginationButton = component$(
  ({ for: htmlFor, class: className }: SidebarPaginationButtonProps) => {
    return (
      <label
        class={[
          "sgtv-flex sgtv-h-6 sgtv-w-6 sgtv-cursor-pointer sgtv-items-center sgtv-justify-center sgtv-border sgtv-border-neutral-200 sgtv-text-gray-400 hover:sgtv-border-accent-500 hover:sgtv-bg-accent-500 hover:sgtv-text-white",
          className,
        ]}
        for={htmlFor}
      >
        <Slot />
      </label>
    );
  },
);

const SidebarPaginationLeftButton = ({
  for: htmlFor,
  class: className,
}: SidebarPaginationButtonProps) => {
  return (
    <SidebarPaginationButton for={htmlFor} class={className}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width={1.5}
        stroke="currentColor"
        class="sgtv-size-2.5"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M15.75 19.5 8.25 12l7.5-7.5"
        />
      </svg>
    </SidebarPaginationButton>
  );
};

const SidebarPaginationRightButton = ({
  for: htmlFor,
  class: className,
}: SidebarPaginationButtonProps) => {
  return (
    <SidebarPaginationButton for={htmlFor} class={className}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width={1.5}
        stroke="currentColor"
        class="sgtv-size-2.5"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="m8.25 4.5 7.5 7.5-7.5 7.5"
        />
      </svg>
    </SidebarPaginationButton>
  );
};

/**
 * Supports up to 6 containers
 */
export const SidebarMovies = ({
  items,
  class: className,
}: SidebarMoviesProps) => {
  return (
    <div class={["sgtv-group/wrapper", className]}>
      {items.map((group, groupIndex) => {
        return (
          <div
            key={groupIndex}
            class="sgtv-group/movies sgtv-flex sgtv-flex-col sgtv-flex-wrap sgtv-gap-2"
          >
            <input
              type="radio"
              name="items"
              id={`group-${groupIndex + 1}`}
              checked={groupIndex === 0 ? true : false}
              hidden
            />
            <div
              class={[
                "sgtv-pointer-events-none sgtv-absolute sgtv-left-0 sgtv-top-0 sgtv-flex sgtv-w-full sgtv-flex-wrap sgtv-opacity-0",
                "group-has-[:checked]/movies:sgtv-pointer-events-auto group-has-[:checked]/movies:sgtv-static group-has-[:checked]/movies:sgtv-z-10 group-has-[:checked]/movies:sgtv-opacity-100",
              ]}
              id={`movies-container-${groupIndex + 1}`}
            >
              {group.map((item, itemIndex) => {
                return (
                  <div
                    key={`${item.movieTitle}_${itemIndex}`}
                    class={[
                      "sgtv-w-1/2 sgtv-opacity-50 sgtv-transition-all sgtv-duration-[200ms] sgtv-ease-in sgtv-scale-75 group-has-[:checked]/movies:sgtv-opacity-100 group-has-[:checked]/movies:sgtv-scale-100",
                    ]}
                  >
                    <div
                      class={[
                        "sgtv-h-full sgtv-w-full sgtv-pb-[4%] sgtv-pt-[4%]",
                        (itemIndex + 1) % 2 !== 0
                          ? "sgtv-pr-[4%]"
                          : "sgtv-pl-[4%]",
                      ]}
                    >
                      <SidebarMovieItem {...item} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div
              class={[
                "sgtv-hidden sgtv-gap-1 group-has-[:checked]/movies:sgtv-flex",
                groupIndex === 0
                  ? "group-has-[#movies-container-2:empty]/wrapper:sgtv-hidden"
                  : "",
              ]}
            >
              <SidebarPaginationLeftButton
                for={groupIndex !== 0 ? `group-${groupIndex}` : undefined}
              />
              {groupIndex < 5 ? (
                <SidebarPaginationRightButton
                  for={
                    groupIndex + 2 < 7 ? `group-${groupIndex + 2}` : undefined
                  }
                  class={[
                    groupIndex === 1
                      ? "group-has-[#movies-container-3:empty]/wrapper:sgtv-hidden"
                      : "",
                    groupIndex === 2
                      ? "group-has-[#movies-container-4:empty]/wrapper:sgtv-hidden"
                      : "",
                    groupIndex === 3
                      ? "group-has-[#movies-container-5:empty]/wrapper:sgtv-hidden"
                      : "",
                    groupIndex === 4
                      ? "group-has-[#movies-container-6:empty]/wrapper:sgtv-hidden"
                      : "",
                  ]}
                />
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};
